import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Spinner } from '@amzn/awsui-components-console';
import { useAuth } from '../hooks/useAuth';
import { AccessDeniedPage } from './AccessDeniedPage';
import { featureFlags } from '../config/featureFlags';

/**
 * RequireAuth
 *
 * Route guard that redirects unauthenticated users to the landing page.
 * Shows access denied page if user has no allowlisted channels.
 * Shows a loading spinner while the session is being validated.
 * Renders children if the user is authenticated with channel access.
 *
 * When featureFlags.bypassAuth is true (local dev), renders children immediately.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { authState } = useAuth();

  if (featureFlags.bypassAuth) {
    return <>{children}</>;
  }

  if (authState.loading) {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!authState.hasChannelAccess) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
}
